/**
 * F-005 Option B — image proxy URL builder + SSRF validator.
 *
 * The storefront reads images from the `product-images` and `store-logos`
 * Supabase Storage buckets. Supabase's local CLI Storage does NOT emit
 * `X-Content-Type-Options: nosniff` on public-bucket responses (verified
 * by the Phase 4.5 E2E gate — `apps/storefront/e2e/f-005-storage-nosniff.spec.ts`).
 * To close INFRA-TBD-nosniff, the storefront routes every image load
 * through a public, unauthenticated Deno edge function
 * (`supabase/functions/image-proxy/index.ts`) that re-fetches the object
 * from the public bucket and re-asserts the security headers:
 *
 *   X-Content-Type-Options: nosniff
 *   Content-Type: image/{png,jpeg,webp}   (re-asserted from a whitelist)
 *   Content-Disposition: inline; filename="..."
 *   Cache-Control: public, max-age=3600, immutable
 *
 * This module contains the **pure** pieces of Option B that are shared
 * between the client (`buildImageProxyUrl` is used by
 * `storefront-product.service.ts` and `<StoreHero>`) and the edge
 * function (`validateImageProxyRequest` enforces the SSRF allowlist).
 *
 * IMPORTANT: `supabase/functions/image-proxy/validate.ts` is a BYTE-FOR-BYTE
 * copy of the validator below — Deno edge functions cannot import from the
 * pnpm workspace at build time, so the two files must stay in sync. A test
 * in `image-proxy.test.ts` pins the invariants; if you change the regex or
 * the allowlist, update BOTH files and re-run `pnpm -w test`.
 *
 * Design reference: `.dukanti/designs/F-005.md` §14.1 Option B.
 */

/**
 * Canonical allowlist of public buckets the proxy may forward to. Used by
 * both the client (to build URLs) and the edge function (to reject anything
 * else). Keep this as a frozen string tuple so `typeof ALLOWED_BUCKETS[number]`
 * narrows in TypeScript call sites.
 */
export const ALLOWED_BUCKETS = [
  'product-images',
  'store-logos',
  'category-media',
  'store-ad-media',
  'storefront-media',
  'website-template-assets',
] as const;
export type AllowedBucket = (typeof ALLOWED_BUCKETS)[number];

/**
 * Storage-path regex for the `product-images` bucket. Intentionally
 * STRICTER than the F-003 `product_images_path_chk` DB CHECK constraint,
 * which uses `[^/]+` for the filename stem. The proxy tightens this to
 * `[A-Za-z0-9._-]+` to reject URL-reserved characters (`?`, `#`, `&`,
 * space, `%`) that would corrupt the upstream fetch URL.
 * See: followup F-005-image-proxy-path-tighten.
 *
 * The first path segment is the store_id (a UUID). The filename stem is
 * restricted to `[A-Za-z0-9._-]+` (alphanumerics, dots, underscores,
 * hyphens) to prevent URL-reserved characters from escaping the
 * query string when the edge function constructs the upstream URL. Uppercase
 * hex is allowed because Postgres CHECKs case-sensitive and the constraint
 * was authored with `[0-9a-fA-F]`.
 */
export const PRODUCT_IMAGES_PATH_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/[A-Za-z0-9._-]+\.(jpg|png|webp)$/;

/**
 * Storage-path regex for the `store-logos` bucket. There is no DB CHECK
 * constraint on storage object names for this bucket (F-002 enforces the
 * convention via the `uploadStoreLogo` service helper — see
 * `packages/shared/src/services/store.service.ts` line ~103: the path is
 * built as `${storeId}/logo.${ext}`). The regex below matches that shape:
 *
 *   {store_uuid}/logo.{jpg|jpeg|png|webp}
 *
 * `jpeg` is admitted in addition to `jpg` because the F-002 upload path
 * derives the extension from the file's MIME type and can emit `jpeg`
 * when the browser reports `image/jpeg`. If a future F-002 change pins
 * the extension whitelist, update this regex in both this file and the
 * edge function's `validate.ts` sibling.
 */
export const STORE_LOGOS_PATH_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/(?:logo|logos\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\.(jpg|png|webp)$/;

export const CATEGORY_MEDIA_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const STORE_AD_MEDIA_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const STOREFRONT_MEDIA_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Platform-owned template assets: lowercase key segments, image extensions only. */
export const WEBSITE_TEMPLATE_ASSETS_PATH_RE =
  /^[a-z0-9][a-z0-9-]*(\/[a-z0-9][a-z0-9-]*)*\.(webp|jpg|png)$/;

export type ValidateResult =
  | { readonly ok: true; readonly bucket: AllowedBucket; readonly path: string }
  | { readonly ok: false; readonly status: number; readonly reason: string };

export type ValidateStoreAdMediaProxyResult =
  | { readonly ok: true; readonly adMedia: string }
  | { readonly ok: false; readonly status: number; readonly reason: string };

/**
 * Validate a (bucket, path) pair against the SSRF allowlist. Rejects
 * anything that isn't in `ALLOWED_BUCKETS` and anything whose path
 * doesn't match the bucket's documented convention. Defense-in-depth
 * also rejects traversal characters (`..`, leading `/`, backslash) even
 * though the main regex already excludes them.
 *
 * The error shape is uniform — callers must not leak which check failed
 * to the HTTP response body. The same `invalid request` message is
 * returned regardless of whether the bucket or the path was bad.
 */
export function validateImageProxyRequest(
  bucket: string | null | undefined,
  path: string | null | undefined,
): ValidateResult {
  if (!bucket || !(ALLOWED_BUCKETS as readonly string[]).includes(bucket)) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  if (typeof path !== 'string' || path.length === 0) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  // F-005-image-proxy-length-cap: reject absurdly long paths before the
  // regex runs — a multi-megabyte query string would force allocation + scan.
  if (path.length > 256) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  // Defense-in-depth — reject traversal / absolute paths / Windows separators
  // up-front so the regex only gets called on minimally-sane input.
  if (path.includes('..') || path.startsWith('/') || path.includes('\\')) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  const re =
    bucket === 'product-images'
      ? PRODUCT_IMAGES_PATH_RE
      : bucket === 'store-logos'
        ? STORE_LOGOS_PATH_RE
        : bucket === 'category-media'
          ? CATEGORY_MEDIA_ID_RE
          : bucket === 'store-ad-media'
            ? STORE_AD_MEDIA_ID_RE
            : bucket === 'website-template-assets'
              ? WEBSITE_TEMPLATE_ASSETS_PATH_RE
              : STOREFRONT_MEDIA_ID_RE;
  if (!re.test(path)) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  return { ok: true, bucket: bucket as AllowedBucket, path };
}

export function validateStoreAdMediaProxyRequest(
  adMedia: string | null | undefined,
): ValidateStoreAdMediaProxyResult {
  if (typeof adMedia !== 'string' || adMedia.length === 0 || adMedia.length > 64) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  if (adMedia.includes('..') || adMedia.startsWith('/') || adMedia.includes('\\')) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  if (!STORE_AD_MEDIA_ID_RE.test(adMedia)) {
    return { ok: false, status: 400, reason: 'invalid request' };
  }
  return { ok: true, adMedia };
}

/**
 * Build the public-facing URL for a proxied storage object. This is the
 * URL the storefront's `<img src>` attributes and the service layer's
 * resolved URL strings point at. The edge function lives at
 * `${SUPABASE_URL}/functions/v1/image-proxy` in both local dev
 * (`http://127.0.0.1:54321` / `:64321`) and hosted deploys
 * (`https://<project>.supabase.co`) — the URL base is passed in by the
 * caller (usually `client.supabaseUrl` or a config value) so this helper
 * stays ESM-pure and does not reach into `env.ts`.
 *
 * Both query parameters are `encodeURIComponent`-escaped so that slugs
 * or filenames containing reserved characters (`?`, `&`, `#`, space)
 * cannot escape the query string. The validator above is the source of
 * truth for what paths are LEGAL; this function just escapes them.
 */
export function buildImageProxyUrl(
  supabaseUrl: string,
  bucket: AllowedBucket,
  path: string,
): string {
  // Normalize a single trailing slash on the base so we never emit
  // `http://host//functions/...`. The regex is deliberately anchored.
  const base = supabaseUrl.replace(/\/$/, '');
  const qBucket = encodeURIComponent(bucket);
  const qPath = encodeURIComponent(path);
  return `${base}/functions/v1/image-proxy?bucket=${qBucket}&path=${qPath}`;
}

/**
 * Build a storefront-builder media URL without exposing the private
 * `storefront-media` bucket name or raw storage path. The image-proxy edge
 * function resolves this opaque asset id server-side.
 */
export function buildStorefrontMediaProxyUrl(supabaseUrl: string, mediaAssetId: string): string {
  const base = supabaseUrl.replace(/\/$/, '');
  return `${base}/functions/v1/image-proxy?media=${encodeURIComponent(mediaAssetId)}`;
}

/**
 * Build a Storefront Engine promotional-ad image URL without exposing the
 * private `store-ad-media` bucket name or a `path` query parameter. The
 * image-proxy Edge function resolves the opaque ad id server-side.
 */
export function buildStoreAdMediaProxyUrl(supabaseUrl: string, adId: string): string {
  const base = supabaseUrl.replace(/\/$/, '');
  return `${base}/functions/v1/image-proxy?adMedia=${encodeURIComponent(adId)}`;
}

/**
 * Response Content-Type allowlist. The edge function re-asserts the
 * Content-Type header from this whitelist (never trusting the upstream
 * blindly). Anything else → 415.
 */
export const ALLOWED_IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ALLOWED_CATEGORY_MEDIA_CONTENT_TYPES = [
  ...ALLOWED_IMAGE_CONTENT_TYPES,
  'image/gif',
] as const;
export const ALLOWED_STORE_AD_MEDIA_CONTENT_TYPES = [
  ...ALLOWED_IMAGE_CONTENT_TYPES,
  'image/gif',
] as const;
export type AllowedImageContentType =
  | (typeof ALLOWED_IMAGE_CONTENT_TYPES)[number]
  | (typeof ALLOWED_CATEGORY_MEDIA_CONTENT_TYPES)[number]
  | (typeof ALLOWED_STORE_AD_MEDIA_CONTENT_TYPES)[number];

/**
 * Normalize an upstream `Content-Type` header to a whitelisted value, or
 * `null` if it doesn't match. Strips any `; charset=...` tail so a
 * response like `image/jpeg; charset=binary` still resolves. Also maps
 * `image/jpg` → `image/jpeg` because some older Supabase Storage
 * configurations historically emit that vendor-invalid spelling.
 */
export function resolveAllowedContentType(
  upstream: string | null | undefined,
): AllowedImageContentType | null;
export function resolveAllowedContentType(
  bucket: AllowedBucket,
  upstream: string | null | undefined,
): AllowedImageContentType | null;
export function resolveAllowedContentType(
  bucketOrUpstream: AllowedBucket | string | null | undefined,
  maybeUpstream?: string | null | undefined,
): AllowedImageContentType | null {
  const bucket: AllowedBucket =
    maybeUpstream === undefined ? 'product-images' : (bucketOrUpstream as AllowedBucket);
  const upstream = maybeUpstream === undefined ? bucketOrUpstream : maybeUpstream;
  if (!upstream || typeof upstream !== 'string') return null;
  // Strip parameters (e.g. `; charset=binary`).
  const head = upstream.split(';', 1)[0]?.trim().toLowerCase();
  if (!head) return null;
  const normalized = head === 'image/jpg' ? 'image/jpeg' : head;
  const allowedTypes =
    bucket === 'category-media' || bucket === 'store-ad-media'
      ? ALLOWED_STORE_AD_MEDIA_CONTENT_TYPES
      : ALLOWED_IMAGE_CONTENT_TYPES;
  if ((allowedTypes as readonly string[]).includes(normalized)) {
    return normalized as AllowedImageContentType;
  }
  return null;
}
